/**
 * 双重预防机制 - 风险治理管理 API
 * 包括：抽检任务执行、抽检任务整改、整改监管、抽检任务查询
 */
import request from "@/utils/enhancedRequest";

// ==================== 抽检任务执行 ====================

/**
 * 获取抽检任务列表（分页）
 * @param {Object} data - 查询参数
 * @param {String} data.pointCode - 点位编码
 * @param {Number} data.type - 类型 0点位抽检 1计划抽检 2随机抽检
 * @param {Number} data.status - 状态 0待执行 1执行中 2已完成
 * @param {String} data.name - 模糊查询 风险点位名称
 * @param {Number} data.queryType - 查询类型 0抽检任务执行 1抽检任务查询
 * @param {String} data.startTime - 开始时间
 * @param {String} data.endTime - 结束时间
 * @param {Number} data.pageNo - 页码
 * @param {Number} data.pageSize - 每页数量
 * @returns {Promise}
 */
export function getSamplingTaskPage(data) {
  return request({
    url: `/sa/green/pre/risk-point-sampling-tasks/page`,
    method: "post",
    data,
  });
}

/**
 * 获取抽检任务详情
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function getSamplingTaskDetail(id) {
  return request({
    url: `/sa/safety/samplingTask/detail/${id}`,
    method: "get",
  });
}

/**
 * 获取抽检任务数据（用于编辑）
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function getSamplingTaskById(id) {
  return request({
    url: `/sa/green/pre/risk-point-sampling-tasks/get`,
    method: "get",
    params: { id },
  });
}



/**
 * 创建抽检任务
 * @param {Object} data - 任务数据
 * @returns {Promise}
 */
export function createSamplingTask(data) {
  return request({
    url: `/sa/green/pre/risk-point-sampling-tasks/create`,
    method: "post",
    data,
  });
}

/**
 * 执行抽检任务
 * @param {Object} data - 执行数据
 * @returns {Promise}
 */
export function executeSamplingTask(data) {
  return request({
    url: `/sa/safety/samplingTask/execute`,
    method: "post",
    data,
  });
}

/**
 * 完成抽检任务
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function completeSamplingTask(id) {
  return request({
    url: `/sa/safety/samplingTask/complete/${id}`,
    method: "post",
  });
}

/**
 * 更新抽检任务
 * @param {Object} data - 任务数据
 * @returns {Promise}
 */
export function updateSamplingTask(data) {
  return request({
    url: `/sa/green/pre/risk-point-sampling-tasks/update`,
    method: "put",
    data,
  });
}

/**
 * 删除抽检任务
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function deleteSamplingTask(id) {
  return request({
    url: `/sa/green/pre/risk-point-sampling-tasks/delete`,
    method: "delete",
    params: { id },
  });
}

/**
 * 获取抽检任务点位因素详情
 * @param {String} id - 因素ID
 * @returns {Promise}
 */
export function getRiskFactorDetail(id) {
  return request({
    url: `/sa/green/pre/risk-point-sampling-tasks/getFactorItem`,
    method: "get",
    params: { id },
  });
}

// ==================== 抽检任务整改 ====================

/**
 * 获取抽检整改任务列表（分页）
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getSamplingRectificationPage(data) {
  return request({
    url: `/sa/safety/samplingRectification/page`,
    method: "post",
    data,
  });
}

/**
 * 获取抽检整改任务详情
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function getSamplingRectificationDetail(id) {
  return request({
    url: `/sa/safety/samplingRectification/detail/${id}`,
    method: "get",
  });
}

/**
 * 提交抽检整改
 * @param {Object} data - 整改数据
 * @returns {Promise}
 */
export function submitSamplingRectification(data) {
  return request({
    url: `/sa/safety/samplingRectification/submit`,
    method: "post",
    data,
  });
}

/**
 * 完成抽检整改
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function completeSamplingRectification(id) {
  return request({
    url: `/sa/safety/samplingRectification/complete/${id}`,
    method: "post",
  });
}

// ==================== 整改监管 ====================

/**
 * 获取抽检整改监管列表（分页）
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getSamplingSupervisionPage(data) {
  return request({
    url: `/sa/safety/samplingSupervision/page`,
    method: "post",
    data,
  });
}

/**
 * 审核抽检整改
 * @param {Object} data - 审核数据
 * @returns {Promise}
 */
export function approveSamplingRectification(data) {
  return request({
    url: `/sa/safety/samplingSupervision/approve`,
    method: "post",
    data,
  });
}

/**
 * 拒绝抽检整改
 * @param {Object} data - 拒绝数据
 * @returns {Promise}
 */
export function rejectSamplingRectification(data) {
  return request({
    url: `/sa/safety/samplingSupervision/reject`,
    method: "post",
    data,
  });
}

// ==================== 抽检任务查询 ====================

/**
 * 获取抽检任务查询列表（分页）
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getSamplingTaskQueryPage(data) {
  return request({
    url: `/sa/safety/samplingTask/queryPage`,
    method: "post",
    data,
  });
}

/**
 * 获取风险治理统计数据
 * @param {Object} data - 查询参数
 * @param {String} data.projectId - 当前用户项目id
 * @param {String} data.startDate - 开始日期
 * @param {String} data.endDate - 结束日期
 * @returns {Promise}
 */
export function getGovernanceStatistics(data) {
  return request({
    url: `/sa/green/pre/risk-point-sampling-tasks/governanceStatistics`,
    method: "post",
    data,
  });
}

/**
 * 导出抽检任务
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function exportSamplingTask(data) {
  return request({
    url: `/sa/green/pre/risk-point-sampling-tasks/export`,
    method: "post",
    data,
  });
}

// ==================== 系统外抽检任务 ====================

/**
 * 创建系统外抽检任务
 */
export function createOutSideSamplingTask(data) {
  return request({ url: `/sa/green/pre/out-side-sampling-tasks/create`, method: "post", data });
}

/**
 * 更新系统外抽检任务
 */
export function updateOutSideSamplingTask(data) {
  return request({ url: `/sa/green/pre/out-side-sampling-tasks/update`, method: "put", data });
}

/**
 * 删除系统外抽检任务
 */
export function deleteOutSideSamplingTask(id) {
  return request({ url: `/sa/green/pre/out-side-sampling-tasks/delete`, method: "delete", params: { id } });
}

/**
 * 获取系统外抽检任务详情
 */
export function getOutSideSamplingTaskById(id) {
  return request({ url: `/sa/green/pre/out-side-sampling-tasks/get`, method: "get", params: { id } });
}

/**
 * 系统外抽检任务分页列表
 * queryType: 0 系统外抽检任务执行  1 系统外抽检任务查询
 */
export function getOutSideSamplingTaskPage(data) {
  return request({ url: `/sa/green/pre/out-side-sampling-tasks/page`, method: "post", data });
}

/**
 * 导出系统外抽检任务
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function exportOutSideSamplingTask(data) {
  return request({
    url: `/sa/green/pre/out-side-sampling-tasks/export`,
    method: "post",
    data,
  });
}

// ==================== 系统外整改 ====================

/**
 * 系统外整改提交
 */
export function outSideTaskRectification(data) {
  return request({ url: `/sa/green/pre/out-side-tasks-rectification/rectification`, method: "put", data });
}

/**
 * 系统外整改确认
 */
export function confirmOutSideTaskRectification(data) {
  return request({ url: `/sa/green/pre/out-side-tasks-rectification/confirm`, method: "put", data });
}

/**
 * 系统外整改详情
 */
export function getOutSideTaskRectificationById(id) {
  return request({ url: `/sa/green/pre/out-side-tasks-rectification/get`, method: "get", params: { id } });
}

/**
 * 系统外整改分页列表
 * queryType: 0 系统外抽检任务整改  1 系统外抽检任务整改监管
 */
export function getOutSideTaskRectificationPage(data) {
  return request({ url: `/sa/green/pre/out-side-tasks-rectification/page`, method: "post", data });
}

